import React, { useState, useRef, useEffect } from "react";
import { Mic, Pause, PlayArrow } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RecordRTC from "recordrtc";
import SwipeButton from "./SwipeButton/SwipeButton";
import { getAudioFileName, getTodaysDate } from "../utils/utils";

const AudioRecording = ({ showRecordPopup, setShowRecordPopup,audioPopupNumber, setAudioPopupNumber,setFormData,categories }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // For pausing
  const [isStopped, setIsStopped] = useState(false); // For stopping
  const [audioBlob, setAudioBlob] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState(null); // To store permission error
  const [time, setTime] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  const [recordLoading,setRecordLoading] = useState(false)
  const timerRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null); // To hold the media stream
  const audioRef = useRef(null); // Use a ref for the audio element

  const [fileData, setFileData] = useState({
    filename: "",
    category: "",
    id: "",
  });
  const [audioURL, setAudioURL] = useState(null); // To store the audio URL for the player
  const [audioDuration, setAudioDuration] = useState(null); // To store the formatted duration

  // Request permission on component load
  useEffect(() => {
    const getAudioPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        streamRef.current = stream; // Save the stream for later use
        setHasPermission(true); // Set permission to true once granted
      } catch (err) {
        console.error(`Permission error: ${err}`);
        setError(
          "Microphone access is denied. Please allow microphone access."
        );
        setHasPermission(false); // Set permission to false if denied
      }
    };

    getAudioPermission();
  }, []);

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(
      2,
      "0"
    )}:${String(secs).padStart(2, "0")}`;
  };

  const formatTime = ({ hours, minutes, seconds, milliseconds }) => {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}:${String(milliseconds / 10).padStart(
      2,
      "0"
    )}`;
  };


  const startRecording = () => {
    if (!hasPermission) {
      console.error("No permission to access microphone");
      setError("No permission to access microphone");
      return;
    }

    if (isStopped) {
      console.error("Recording is already stopped. Cannot restart.");
      return;
    }

    setIsRecording(true);
    setIsPaused(false); // Resume if paused

    // Start timer
    timerRef.current = setInterval(() => {
      setTime((prevTime) => {
        const ms = prevTime.milliseconds + 10;
        const sec = prevTime.seconds + Math.floor(ms / 1000);
        const min = prevTime.minutes + Math.floor(sec / 60);
        const hrs = prevTime.hours + Math.floor(min / 60);

        return {
          hours: hrs,
          minutes: min % 60,
          seconds: sec % 60,
          milliseconds: ms % 1000,
        };
      });
    }, 10); // update every 10 milliseconds

    // Start or resume recording
    if (!recorderRef.current) {
      recorderRef.current = new RecordRTC(streamRef.current, {
        type: "audio",
        mimeType: "audio/webm",
      });
      recorderRef.current.startRecording();
    } else {
      recorderRef.current.resumeRecording(); // Resume if paused
    }
  };

  const pauseRecording = () => {
    if (isStopped) {
      console.error("Recording is stopped. Cannot pause.");
      return;
    }
    setIsPaused(true);
    clearInterval(timerRef.current); // Stop the timer
    recorderRef.current.pauseRecording(); // Pause recording
  };

  const handleChange = (event, value, name) => {
    if (event) {
      let { name, value } = event.target;
      setFileData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFileData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const stopRecording = () => {
    setIsRecording(false);
    setIsStopped(true);
    setFileData((prev)=>({...prev, filename:getAudioFileName()}))

    clearInterval(timerRef.current);

    recorderRef?.current?.stopRecording(() => {
      const blob = recorderRef.current.getBlob();
      setAudioBlob(blob);

      // Create an audio element to determine the duration and provide a player
      const url = URL.createObjectURL(blob);
      setAudioURL(url); // Set the audio URL for the player

      const audio = new Audio(url);
      audio.onloadedmetadata = () => {
        const duration = audio.duration; // Get duration in seconds
        const formattedDuration = formatDuration(duration); // Format duration to hh:mm:ss
        setAudioDuration(formattedDuration); // Set formatted duration
      };
      
      recorderRef.current = null;
    });
  };

  useEffect(() => {
    if (audioRef.current && audioURL) {
      audioRef.current.load(); // Reload audio if audioURL changes
    }
  }, [audioURL]);

  const handleCloseRecording = () => {
    if (recorderRef.current) {
      recorderRef?.current?.stopRecording(() => {});
      recorderRef.current = null; // Reset the recorder reference
      // streamRef.current?.getTracks().forEach(track => track.stop()); // Stop the media stream

    }

    setIsPaused(false);
    setIsRecording(false)
    setIsStopped(false);
    setAudioBlob(null)
    setShowRecordPopup(false);

    clearInterval(timerRef.current);
    setTime({
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    })
    setFileData({
      filename: "",
      category: "",
      id: "",
    })
    setRecordLoading(false)
  }

  const sendToBackend = async () => {
    console.log("AUDIO BLOB", audioBlob);
    
    if (!audioBlob) return;
    setRecordLoading(true)
    let fileName = fileData?.filename?.length > 0 ? fileData.filename : "audio_recording"
    const formData = new FormData();
    formData.append( "audioBlob", audioBlob); // or 'audio_recording.wav'
    formData.append( "fileName", `${fileName}.webm`); // or 'audio_recording.wav'

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/upload-audio`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setFormData((prev)=>({
          ...prev,
          [`File ${audioPopupNumber} Name`]: fileData?.filename,
          [`File ${audioPopupNumber} Category`]:fileData?.category,
          [`File ${audioPopupNumber} URL`]:result?.url,
          [`File ${audioPopupNumber} Date`]: getTodaysDate(),
          
        }))
       handleCloseRecording()
        console.log("File uploaded:", result);
        
      } else {
        console.error("Failed to upload file:", response.statusText);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }finally{
      setRecordLoading(false)
    }
  };

  return (
    <Dialog open={showRecordPopup} className="audio_input_dialog_main">
      <DialogTitle>Record Audio</DialogTitle>
      <DialogContent>
        <Container
          style={{ height: "100%" }}
          className="AudioRecording_Container"
        >
          <Box className="recording_button_wrapper">
            <Box
              className={`${
                isRecording
                  ? "recording_on mic_icon_wrapper"
                  : "mic_icon_wrapper"
              } ${isStopped ? "recording_stopped" : ""}`}
              onClick={() => {
                if (isStopped) return; // Prevent any action if recording is stopped
                if (isPaused) {
                  startRecording(); // Pause when recording
                } else if (isRecording) {
                  pauseRecording(); // Resume when paused
                } else {
                  startRecording(); // Start recording
                }
              }}
            >
              {isRecording || isPaused ? (
                isPaused ? (
                  <PlayArrow />
                ) : (
                  <Pause />
                )
              ) : (
                <Mic />
              )}
            </Box>

            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={2}
              className="recording_button_container"
            ></Stack>

            {isRecording || isPaused ? (
              <Typography variant="caption">{formatTime(time)}</Typography>
            ) : (
              <>
                {error && (
                  <Typography variant="caption" color="error">
                    {error}
                  </Typography>
                )}
              </>
            )}

            {/* Stop recording using swipe button */}
            {(isRecording || isPaused || isStopped) && (
              <SwipeButton onStop={stopRecording} isPaused={isPaused} isStopped={isStopped} isRecording={isRecording} />
            )}
            {/* {isStopped && audioURL && (
          <Box mt={2}>
            <audio ref={audioRef} controls src={audioURL} />
          </Box>
        )} */}
            {isStopped && (
              <Box width={"90%"}>
                <FormControl fullWidth>
                  <InputLabel id="recording-file-name-input">
                    File Name
                  </InputLabel>
                  <OutlinedInput
                    id="outlined-adornment-weight"
                    label="FileName"
                    value={fileData?.filename}
                    endAdornment={
                      <InputAdornment position="end">.webm</InputAdornment>
                    }
                    aria-describedby="recording-file-name-input"
                    name="filename"
                    onChange={handleChange}
                  />
                </FormControl>
                <Autocomplete
                  sx={{ mt: 2 }}
                  id="free-solo-demo"
                  freeSolo
                  fullWidth
                  name="category"
                  options={categories}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      onChange={handleChange}
                    />
                  )}
                  onChange={(event, value) =>
                    handleChange(null, value, "category")
                  }
                />
                {/* <Box className="recording_action_button_container">
             
            </Box> */}
              </Box>
            )}
          </Box>
        </Container>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          className="recording_save_button"
          onClick={() => {
        
            handleCloseRecording()
          }}

        >
          Cancel
        </Button>
        <Button
          variant="contained"
          className="recording_save_button"
          onClick={sendToBackend}
        >
          {recordLoading ? <CircularProgress/> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AudioRecording;
